"use client";

import { ChevronLeft, ChevronRight } from "lucide-react";
import { type ReactNode, type RefObject, useLayoutEffect, useRef } from "react";
import { cn } from "../../lib";
import { Button } from "../button";
import {
	HEX_TILE_BACKGROUND_SUBTLE_WASH_OPACITY,
	HexTileBackground,
} from "../custom/hextile-background";
import {
	SCOPED_PANEL_HOST_Z_INDEX,
	SCOPED_PANEL_OVERLAY_NAV_BUTTON_PX,
	SCOPED_PANEL_OVERLAY_NAV_Z_INDEX,
	SCOPED_PANEL_PANEL_Z_INDEX,
	scopedPanelOverlayClassName,
} from "./constants";
import {
	type ScopedPanelSide,
	scopedPanelEdgeChromeClassName,
	scopedPanelSlideInClassName,
} from "./scoped-panel-chrome";

export interface ScopedPanelOverlayNavProps {
	canGoPrev: boolean;
	canGoNext: boolean;
	onPrev?: () => void;
	onNext?: () => void;
	prevAriaLabel: string;
	nextAriaLabel: string;
}

/** Occupied height from the host top/bottom edges to the map band between sheets. */
export interface ScopedPanelSheetInsets {
	topPx: number;
	bottomPx: number;
}

export interface ScopedPanelHostProps {
	open: boolean;
	onDismiss?: () => void;
	/** Accessible label for the panel group (e.g. stop preview). */
	ariaLabel: string;
	topPanel?: ReactNode;
	bottomPanel?: ReactNode;
	leftPanel?: ReactNode;
	rightPanel?: ReactNode;
	/** Prev/next orbs vertically centered on the overlay flanks. */
	overlayNav?: ScopedPanelOverlayNavProps;
	/** Fired when top/bottom sheet layout changes (e.g. for map viewport centering). */
	onSheetInsetsChange?: (insets: ScopedPanelSheetInsets) => void;
	/** When set, insets are measured against this root (e.g. the pan-zoom viewport). */
	layoutMeasurementRootRef?: RefObject<HTMLElement | null>;
	className?: string;
	overlayClassName?: string;
	/**
	 * When false, only panels render (dim + dismiss live on the map viewport so
	 * spotlights at 55+ stay above dim at 48). Do not enable with a visible overlay
	 * on the host (z 60) — it stacks over map spotlights.
	 */
	showOverlay?: boolean;
}

interface ScopedPanelSlotProps {
	side: ScopedPanelSide;
	children: ReactNode;
	className?: string;
	slotRef?: RefObject<HTMLDivElement | null>;
}

const scopedPanelCompactSlotClass =
	"@max-[39.999rem]/org-app-shell:p-0 @max-[39.999rem]/org-app-shell:w-full";

/** Matches {@link ScopedPanelSurface} `max-w-lg` so top/bottom sheets stay aligned. */
const scopedPanelSlotInteractiveClass =
	"pointer-events-auto w-full max-w-lg @max-[39.999rem]/org-app-shell:max-w-none";

/** Matches learn duration/topic badge pills (`border-border`); centered shadow (no offset). */
const scopedPanelOverlayNavButtonClassName = cn(
	"shrink-0 rounded-full border border-border bg-white transition-colors hover:bg-slate-50 active:bg-slate-100",
	"shadow-[0_0_8px_rgba(0,0,0,0.07)] dark:bg-zinc-900 dark:shadow-[0_0_8px_rgba(0,0,0,0.28)]",
	"dark:hover:bg-zinc-800 dark:active:bg-zinc-700",
	"group",
);

export function ScopedPanelSurface({
	side,
	children,
	className,
	contentClassName,
}: ScopedPanelSlotProps & { contentClassName?: string }) {
	return (
		<HexTileBackground
			fillContainer={false}
			opaqueBase
			washOpacity={HEX_TILE_BACKGROUND_SUBTLE_WASH_OPACITY}
			className={cn(
				"overflow-hidden text-foreground",
				scopedPanelEdgeChromeClassName(side),
				scopedPanelSlideInClassName(side),
				"@max-[39.999rem]/org-app-shell:w-full @max-[39.999rem]/org-app-shell:max-w-none @max-[39.999rem]/org-app-shell:rounded-none",
				className,
			)}
			contentClassName={cn("relative", contentClassName)}
		>
			{children}
		</HexTileBackground>
	);
}

function ScopedPanelSlot({
	side,
	children,
	className,
	slotRef,
}: ScopedPanelSlotProps) {
	const positionClass =
		side === "top"
			? "inset-x-0 top-0"
			: side === "bottom"
				? "inset-x-0 bottom-0"
				: side === "left"
					? "inset-y-0 left-0"
					: "inset-y-0 right-0";

	return (
		<div
			ref={slotRef}
			className={cn(
				"pointer-events-none absolute flex w-full justify-center p-3",
				scopedPanelCompactSlotClass,
				"@max-[39.999rem]/org-app-shell:items-stretch",
				positionClass,
				className,
			)}
			style={{ zIndex: SCOPED_PANEL_PANEL_Z_INDEX }}
		>
			<div
				className={scopedPanelSlotInteractiveClass}
				data-scoped-panel-sheet-panel=""
			>
				{children}
			</div>
		</div>
	);
}

/**
 * In-container panel host: one optional dim overlay + multiple edge panels (e.g. top + bottom).
 * Panels use {@link ScopedPanelSurface} (hex tile wash + directional border/shadow).
 */
export function ScopedPanelOverlayNavButton({
	direction,
	disabled,
	onClick,
	ariaLabel,
	size = "overlay",
}: {
	direction: "prev" | "next";
	disabled: boolean;
	onClick?: () => void;
	ariaLabel: string;
	size?: "overlay" | "compact";
}) {
	const Icon = direction === "prev" ? ChevronLeft : ChevronRight;
	const dimensionPx =
		size === "compact" ? 32 : SCOPED_PANEL_OVERLAY_NAV_BUTTON_PX;
	const iconClass =
		size === "compact"
			? "size-4 text-slate-600 transition-colors group-hover:text-slate-900 dark:text-zinc-300 dark:group-hover:text-white"
			: "size-6 text-slate-600 transition-colors group-hover:text-slate-900 dark:text-zinc-300 dark:group-hover:text-white";

	return (
		<Button
			type="button"
			size="icon"
			className={cn(
				scopedPanelOverlayNavButtonClassName,
				disabled ? "cursor-not-allowed opacity-30" : "opacity-100",
			)}
			style={{
				width: dimensionPx,
				height: dimensionPx,
			}}
			aria-disabled={disabled}
			data-disabled={disabled}
			tabIndex={disabled ? -1 : 0}
			aria-label={ariaLabel}
			onClick={() => {
				if (disabled) {
					return;
				}
				onClick?.();
			}}
		>
			<Icon className={iconClass} />
		</Button>
	);
}

function useScopedPanelSheetInsetsMeasurement({
	hostRef,
	topSlotRef,
	bottomSlotRef,
	layoutMeasurementRootRef,
	onSheetInsetsChange,
}: {
	hostRef: RefObject<HTMLDivElement | null>;
	topSlotRef: RefObject<HTMLDivElement | null>;
	bottomSlotRef: RefObject<HTMLDivElement | null>;
	layoutMeasurementRootRef?: RefObject<HTMLElement | null>;
	onSheetInsetsChange?: (insets: ScopedPanelSheetInsets) => void;
}) {
	const onSheetInsetsChangeRef = useRef(onSheetInsetsChange);
	onSheetInsetsChangeRef.current = onSheetInsetsChange;

	useLayoutEffect(() => {
		if (!onSheetInsetsChangeRef.current) {
			return;
		}
		const host = hostRef.current;
		const topSlot = topSlotRef.current;
		const bottomSlot = bottomSlotRef.current;
		if (!host || !topSlot || !bottomSlot) {
			return;
		}

		let lastTopPx = Number.NaN;
		let lastBottomPx = Number.NaN;

		const measure = () => {
			const layoutRoot = layoutMeasurementRootRef?.current ?? host;
			const rootRect = layoutRoot.getBoundingClientRect();
			const topPanel = topSlot.querySelector(
				"[data-scoped-panel-sheet-panel]",
			);
			const bottomPanel = bottomSlot.querySelector(
				"[data-scoped-panel-sheet-panel]",
			);
			const topRect =
				topPanel?.getBoundingClientRect() ??
				topSlot.getBoundingClientRect();
			const bottomRect =
				bottomPanel?.getBoundingClientRect() ??
				bottomSlot.getBoundingClientRect();
			const topPx = Math.max(
				0,
				Math.round(topRect.bottom - rootRect.top),
			);
			const bottomPx = Math.max(
				0,
				Math.round(rootRect.bottom - bottomRect.top),
			);
			if (topPx === lastTopPx && bottomPx === lastBottomPx) {
				return;
			}
			lastTopPx = topPx;
			lastBottomPx = bottomPx;
			onSheetInsetsChangeRef.current?.({ topPx, bottomPx });
		};

		measure();
		const observer = new ResizeObserver(measure);
		observer.observe(host);
		observer.observe(topSlot);
		observer.observe(bottomSlot);
		return () => observer.disconnect();
	}, [bottomSlotRef, hostRef, layoutMeasurementRootRef, topSlotRef]);
}

export function ScopedPanelHost({
	open,
	onDismiss,
	ariaLabel,
	topPanel,
	bottomPanel,
	leftPanel,
	rightPanel,
	overlayNav,
	onSheetInsetsChange,
	layoutMeasurementRootRef,
	className,
	overlayClassName,
	showOverlay = false,
}: ScopedPanelHostProps) {
	const hostRef = useRef<HTMLDivElement>(null);
	const topSlotRef = useRef<HTMLDivElement>(null);
	const bottomSlotRef = useRef<HTMLDivElement>(null);

	useScopedPanelSheetInsetsMeasurement({
		hostRef,
		topSlotRef,
		bottomSlotRef,
		layoutMeasurementRootRef,
		onSheetInsetsChange: open ? onSheetInsetsChange : undefined,
	});

	if (!open) {
		return null;
	}

	return (
		<div
			ref={hostRef}
			className={cn("pointer-events-none absolute inset-0", className)}
			style={{ zIndex: SCOPED_PANEL_HOST_Z_INDEX }}
			role="group"
			aria-label={ariaLabel}
		>
			{showOverlay && onDismiss ? (
				<button
					type="button"
					className={cn(
						"pointer-events-auto absolute inset-0",
						scopedPanelOverlayClassName,
						overlayClassName,
					)}
					style={{ zIndex: SCOPED_PANEL_HOST_Z_INDEX }}
					aria-label="Dismiss"
					onClick={onDismiss}
				/>
			) : null}
			{topPanel ? (
				<ScopedPanelSlot side="top" slotRef={topSlotRef}>
					{topPanel}
				</ScopedPanelSlot>
			) : null}
			{bottomPanel ? (
				<ScopedPanelSlot side="bottom" slotRef={bottomSlotRef}>
					{bottomPanel}
				</ScopedPanelSlot>
			) : null}
			{leftPanel ? (
				<ScopedPanelSlot side="left">{leftPanel}</ScopedPanelSlot>
			) : null}
			{rightPanel ? (
				<ScopedPanelSlot side="right">{rightPanel}</ScopedPanelSlot>
			) : null}
			{overlayNav ? (
				<>
					<div
						className={cn(
							"pointer-events-none absolute inset-y-0 left-0 hidden items-center pl-3 md:pl-6",
							"@min-[40rem]/org-app-shell:flex",
						)}
						style={{ zIndex: SCOPED_PANEL_OVERLAY_NAV_Z_INDEX }}
					>
						<div className="pointer-events-auto">
							<ScopedPanelOverlayNavButton
								direction="prev"
								disabled={!overlayNav.canGoPrev}
								onClick={overlayNav.onPrev}
								ariaLabel={overlayNav.prevAriaLabel}
							/>
						</div>
					</div>
					<div
						className={cn(
							"pointer-events-none absolute inset-y-0 right-0 hidden items-center pr-3 md:pr-6",
							"@min-[40rem]/org-app-shell:flex",
						)}
						style={{ zIndex: SCOPED_PANEL_OVERLAY_NAV_Z_INDEX }}
					>
						<div className="pointer-events-auto">
							<ScopedPanelOverlayNavButton
								direction="next"
								disabled={!overlayNav.canGoNext}
								onClick={overlayNav.onNext}
								ariaLabel={overlayNav.nextAriaLabel}
							/>
						</div>
					</div>
				</>
			) : null}
		</div>
	);
}
